import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { subscribeToPreferences } from '../lib/preferencesChannel';
import { setSignedInUser, clearSignedInUser } from '../instrument';

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
  openAuthModal: () => {},
  closeAuthModal: () => {},
  isAuthModalOpen: false,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  // Which pane the modal should show when it opens next: 'login' (default)
  // or 'register'. Callers pass this via openAuthModal('register') so the
  // header's "Create Account" menu item lands directly on the signup pane
  // without the user having to switch tabs inside the modal.
  const [authModalInitialMode, setAuthModalInitialMode] = useState('login');

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        console.error('[Auth] /auth/me failed:', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Keep Sentry's user context in lockstep with our auth state so
  // every event fired during a signed-in session is tagged with the
  // stable user_id (no email — we set `send_default_pii=false` and
  // scrub email in `instrument.js`). This one hook covers login,
  // logout, and cross-tab refresh — anywhere `user` moves.
  useEffect(() => {
    if (user && user.user_id) setSignedInUser(user);
    else clearSignedInUser();
  }, [user]);

  // Cross-tab sync: when another tab saves a user-level preference, patch
  // the local user object so this tab reflects the change instantly. Only
  // known preference keys are applied — arbitrary keys are ignored so a
  // malicious page in the same origin (or a stale build) can't scribble
  // over unrelated fields.
  useEffect(() => {
    const KNOWN_KEYS = new Set([
      'name',
      'zip_code',
      'narration_preference',
      'default_home_tab',
      'picture',
    ]);
    return subscribeToPreferences(({ key, value }) => {
      if (!KNOWN_KEYS.has(key)) return;
      setUser((prev) => (prev ? { ...prev, [key]: value } : prev));
    });
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, extras = {}) => {
    // `extras` may include { name, gender, religion } — all optional. We
    // forward everything the backend accepts and let it apply defaults
    // for anything omitted.
    const payload = { email, password };
    if (typeof extras === 'string') {
      // Backwards-compat with the old 3-arg (name) signature.
      payload.name = extras;
    } else if (extras && typeof extras === 'object') {
      if (extras.name) payload.name = extras.name;
      if (extras.handle) payload.handle = extras.handle;
      if (extras.gender) payload.gender = extras.gender;
      if (extras.religion) payload.religion = extras.religion;
    }
    const { data } = await api.post('/auth/register', payload);
    // NEW: registration no longer auto-logs the user in. The backend
    // returns the freshly-created user object but does NOT set an
    // auth cookie until they click the verification link. Do NOT
    // `setUser(data)` here — that would trick the UI into thinking
    // they're signed in and let them past the AuthModal.
    return data;
  }, []);

  // Public helper for the /verify-email page to POST the token to the
  // backend. On success the backend sets the auth cookie, so we
  // refresh() locally to pull the freshly-verified user in.
  const verifyEmail = useCallback(async (token) => {
    const { data } = await api.post('/auth/verify-email', { token });
    setUser(data);
    return data;
  }, []);

  // Public helper for "resend verification" buttons on the AuthModal
  // and the /verify-email failure state. Backend returns {ok:true}
  // unconditionally to avoid leaking whether the email exists.
  const resendVerification = useCallback(async (email) => {
    const { data } = await api.post('/auth/resend-verification', { email });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('[Auth] logout failed:', err);
    }
    setUser(null);
  }, []);

  const openAuthModal = useCallback((initialMode) => {
    // Accepts an optional 'login' | 'register' argument. Anything else
    // (including no argument at all) defaults to 'login' so existing
    // call-sites without an argument keep behaving identically.
    setAuthModalInitialMode(initialMode === 'register' ? 'register' : 'login');
    setIsAuthModalOpen(true);
  }, []);
  const closeAuthModal = useCallback(() => setIsAuthModalOpen(false), []);

  const value = useMemo(
    () => ({ user, loading, login, register, verifyEmail, resendVerification, logout, refresh, openAuthModal, closeAuthModal, isAuthModalOpen, authModalInitialMode }),
    [user, loading, login, register, verifyEmail, resendVerification, logout, refresh, openAuthModal, closeAuthModal, isAuthModalOpen, authModalInitialMode]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
